<template>
  <div class="col-xxl-8 col-md-6 col-12">
    <div class="card info-card ps-4 pe-4">
      <div class="card-body driver-card">
        <div class="info">Your information</div>
        <div class="row mt-5">
          <div class="col-12 mb-2">
            <span class="label-title">Driver name</span>
            <div class="content">{{ driverInfo.first_name }}&nbsp;{{ driverInfo.middle_name }}&nbsp;{{ driverInfo.last_name }}</div>
          </div>
          <hr />
          <div class="col-xxl-6 col-12 mt-2">
            <span class="label-title">Driver ID</span>
            <div class="content">{{ driverInfo.driver_id }}</div>
          </div>
          <div class="col-xxl-6 col-12 mt-2">
            <span class="label-title">Terminal number</span>
            <div class="content">{{ driverInfo.terminal_number }}</div>
          </div>
          <hr />
          <div class="col-xxl-6 col-12 mt-2 mb-3 position-relative">
            <span class="label-title">Plate number</span>
            <div class="content">
              {{ driverInfo.plate_number }}
              <button @click="togglePopover('plate', $event)" class="btn btn-link text-primary btn-edit ms-1" ref="editPlateLink">Edit plate</button>
            </div>
            <div v-if="editing === 'plate'" class="popover bs-popover-bottom show" role="tooltip">
              <div class="popover-body">
                <div class="p-3">
                  <div class="box-title">Change your plate number</div>
                  <input type="text" class="form-control mb-3 green-border" v-model="newPlateNumber" />
                  <div class="d-flex justify-content-end">
                    <button class="btn btn-link text-secondary" @click="cancelEdit">Cancel</button>
                    <button class="btn btn-link text-primary btn-ok" @click="savePlateNumber">OK</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-xxl-6 col-12 mt-2 position-relative">
            <span class="label-title">ABN</span>
            <div class="content">
              {{ driverInfo.abn }}
              <button @click="togglePopover('abn', $event)" class="btn btn-link text-primary btn-edit ms-1" ref="editAbnLink">Edit ABN</button>
            </div>
            <div v-if="editing === 'abn'" class="popover bs-popover-bottom show" role="tooltip">
              <div class="popover-body">
                <div class="p-3">
                  <div class="box-title">Change your ABN</div>
                  <input type="text" class="form-control mb-3 green-border" v-model="newAbn" />
                  <div class="d-flex justify-content-end">
                    <button class="btn btn-link text-secondary" @click="cancelEdit">Cancel</button>
                    <button class="btn btn-link text-primary btn-ok" @click="saveAbn">OK</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr />
          <div class="col-12 mt-3">
            <div class="withdraw-bank-title p-2">
              Withdrawal bank account / PayID details
            </div>
            <div class="withdraw-subtitle p-2 mt-2">
              Select the account you wish to nominate for withdrawals. To edit or add details please contact customer support via SMS on <strong>0466 048009</strong> (Monday to Friday 10:30am to 5pm)
            </div>
            <div
              v-for="(account, index) in driverInfo?.accounts"
              :key="account.id"
              class="row account-item mx-0 my-3 align-items-center"
              :class="{ 
                selected: selectedAccountId === account.id,
              }"
              @click="selectAccount(account.id)"
            >
              <!-- 第一欄：勾選框 (綠底勾選) -->
              <div class="col-xxl-2 col-md-4 d-flex align-items-center">
                <div class="check-wrapper" :class="{ checked: selectedAccountId === account.id }">
                 
                  <svg
                    v-if="selectedAccountId === account.id"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 16 16"
                    width="16"
                    height="16"
                    fill="#fff"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M13.854 3.146a.5.5 0 0 1 .0.708l-7.0 7.0a.5.5 0 0 1-.708 0l-3.0-3.0a.5.5 0 1 1 .708-.708l2.646 2.647 6.646-6.647a.5.5 0 0 1 .708 0z"
                    />
                  </svg>
                </div>
              </div>

              <!-- 第二欄：帳號名稱 + 頭像 (被勾選才顯示紫底) -->
              <div class="col-xxl-5 col-md-12 d-flex align-items-center">
                <div class="avatar me-2" :class="{ active: selectedAccountId === account.id }">
                  <!-- 若 nick_name 為 null，預設顯示 'C' -->
                  {{ account.nick_name || 'C' }}
                </div>
                <div class="account-text">
                  <!-- 若 nick_name 為 null，預設顯示 'Current account' -->
                  {{ account.nick_name || 'Current account' }}
                </div>
              </div>

              <!-- 第三欄：顯示 Bank account 或 PayID mobile -->
              <div class="col-xxl-1 col-md-4 bank-text">
                <span v-if="account.type === 1">Bank account</span>
                <span v-if="account.type === 2">PayID mobile</span>
                <span v-if="account.type === 3">PayID email</span>
               
                
                <!-- Auto pay 開關 - 只對卡片類型顯示 -->
                <div v-if="account.type === 4" class="auto-pay-section">
                  <div class="auto-pay-label">Auto pay</div>
                  <div class="auto-pay-toggle">
                    <label class="toggle-switch">
                      <input 
                        type="checkbox" 
                        :checked="account.auto_pay === 1" 
                        :disabled="account.auto_pay === 2"
                        @change="toggleAutoPay(account)"
                      >
                      <span class="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              </div>

              <!-- 第四欄：顯示姓名 & 電話（或其他資訊） -->
              <div class="col-xxl-4 col-md-8 text-end">
                <!-- 若 account_name 為空則顯示一些預設文字，例如 'No name' -->
                <div class="account-detail">{{ account.account_name || 'No Name' }}</div>
                <!-- 若 account_number 為空則顯示 'No number' -->
                <div v-if="account.type === 1" class="account-detail">{{ account.bsb ? account.bsb + ' | ' : '' }}{{ account.account_number}}</div>
                <div v-if="account.type === 2" class="account-detail">{{ account.phone }}</div>
                <div v-if="account.type === 3" class="account-detail">{{ account.email }}</div>
                <div v-if="account.type === 4" class="account-detail">Card ID: {{ account.EAID }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted, computed } from 'vue';
import { useDriverStore } from '@/store/driver/driver';

const driverStore = useDriverStore();
const { fetchDriverInfo, updateDriverInfo, changeAccount } = driverStore;

const editing = ref('');
const newPlateNumber = ref('');
const newAbn = ref('');

// 用 computed 來讀取 driverInfo
const driverInfo = computed(() => driverStore.driverInfo);

const selectedAccountId = ref(null); // 預設勾選第一筆，若不預設則設為 null

const selectAccount = (id) => {
  selectedAccountId.value = id;
  changeAccount(id);
};

// Auto pay 開關處理
const toggleAutoPay = async (account) => {
  // 如果 auto_pay === 2，則不允許切換
  if (account.auto_pay === 2) {
    return;
  }
  
  try {
    // 先切換前端狀態
    const newAutoPayStatus = account.auto_pay === 1 ? 0 : 1;
    account.auto_pay = newAutoPayStatus;
    
    // 直接調用 driverStore 的方法
    await driverStore.updateAutoPay(account.id, newAutoPayStatus);
    
    
  } catch (error) {
    console.error('Error toggling auto pay:', error);
    // 如果 API 調用失敗，恢復原狀態
    account.auto_pay = account.auto_pay === 1 ? 0 : 1;
  }
};

const togglePopover = (type, event) => {
  if (editing.value === type) {
    cancelEdit();
  } else {
    showPopover(type, event);
  }
};

const showPopover = (type, event) => {
  const target = event.target;

  if (type === 'plate') {
    editing.value = 'plate';
    newPlateNumber.value = driverInfo.value.plate_number;
    nextTick(() => {
      const popover = target.nextElementSibling;
      if (popover) {
        popover.style.top = `${target.getBoundingClientRect().bottom + window.scrollY}px`;
        popover.style.left = `${target.getBoundingClientRect().left + window.scrollX}px`;
      }
    });
  } else if (type === 'abn') {
    editing.value = 'abn';
    newAbn.value = driverInfo.value.abn;
    nextTick(() => {
      const popover = target.nextElementSibling;
      if (popover) {
        popover.style.top = `${target.getBoundingClientRect().bottom + window.scrollY}px`;
        popover.style.left = `${target.getBoundingClientRect().left + window.scrollX}px`;
      }
    });
  }
};

const cancelEdit = () => {
  editing.value = '';
};

const savePlateNumber = async () => {
  const updatedFields = {
     abn: driverInfo.value.abn,
     plate_number: newPlateNumber.value  // 同時傳遞當前的車牌號
    };
  await updateDriverInfo(updatedFields);  // 只更新車牌號
  editing.value = '';
};

const saveAbn = async () => {
  const updatedFields = {
    abn: newAbn.value,
    plate_number: driverInfo.value.plate_number  // 同時傳遞當前的車牌號
  };
  await updateDriverInfo(updatedFields);  // 同時更新 ABN 和車牌號
  editing.value = '';
};

onMounted(async () => {
  await fetchDriverInfo();

  const enabledAccount = driverInfo.value?.accounts?.find(acc => acc.enabled == 1);
  if (enabledAccount) {
    selectedAccountId.value = enabledAccount.id;
  } else {
    selectedAccountId.value = null;
  }
});
</script>

<style scoped>
.info-card{
 min-height: 445px;
}

/* 保持樣式代碼不變 */
.text-primary {
  color: rgba(82, 72, 218, 1) !important;
}

.text-primary:hover {
  color: rgba(82, 72, 218, 1) !important;
}

.text-secondary {
  color: #666 !important;
  font-family: Euclid Square;
  font-size: 18px;
  font-weight: 500;
  line-height: 22.82px;
  text-align: right;
}

.position-relative {
  position: relative;
}

.info{
  font-family: Euclid Square;
  font-size: 24px;
  font-weight: 500;
  line-height: 30.43px;
  text-align: left;
}

.label-title{
  font-family: Mulish;
  font-size: 18px;
  font-weight: 400;
  line-height: 22.59px;
  text-align: left;
}

.box-title{
  font-family: Mulish;
  font-size: 18px;
  font-weight: 400;
  line-height: 22.59px;
  text-align: left;
  margin-bottom: 10px;
}

.content{
  font-family: Euclid Square;
  font-size: 42px;
  font-weight: 400;
  line-height: 53.26px;
  text-align: left;
}

.popover {
  max-width: 400px;
  position: absolute;
  z-index: 1000;
  width: 100%; /* 讓彈出框占滿可用寬度 */
  border-radius: 8px; /* 調整邊框圓角 */
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); /* 添加陰影 */
}

.popover-body {
  padding: 1.5rem; /* 增加內邊距 */
  background-color: #fff; /* 背景顏色 */
  border: 1px solid #e0e0e0; /* 邊框顏色 */
  border-radius: 8px; /* 邊框圓角 */
}

.green-border {
  border: 2px solid #00c962 !important; /* 綠色邊框 */
  font-family: Euclid Square;
  font-size: 18.36px;
  font-weight: 400;
  line-height: 23.28px;
  text-align: left;

}

.btn-edit{
  font-family: Euclid Square;
  font-size: 16px;
  font-weight: 400;
  line-height: 20.29px;
  text-align: left;
}

.btn-ok{
  font-family: Euclid Square;
  font-size: 18px;
  font-weight: 500;
  line-height: 22.82px;
  text-align: right;
}

.popover .btn-link {
  color: rgba(82, 72, 218, 1);
  text-decoration: none;
}

.popover .btn-link:hover {
  color: rgba(82, 72, 218, 1);
  text-decoration: underline;
}

.popover-arrow {
  display: none; /* 隱藏箭頭 */
}

.withdraw-title{
  font-family: 'Euclid Square';
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
  line-height: 25px;
  display: flex;
  align-items: center;

  color: #051C2C;
}

.withdraw-text{
  font-family: 'Mulish';
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 23px;

  color: #051C2C;
}
/* 每個帳號區塊外觀 */
.account-item {
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 10px;
  cursor: pointer;
  transition: border 0.3s;
}

/* 被選取時外框顯示綠色 */
.account-item.selected {
  border-color: #00c962;
  border-width: 2px;
}

/* 勾選框外觀 */
.check-wrapper {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  background-color: #fff; /* 預設灰色底 */
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s;
  border: 1px solid #00c962;
}
.check-wrapper.checked {
  background-color: #00c962; /* 綠底勾選 */
}

/* 頭像底色，未勾選則為灰色，勾選後為紫色 */
.avatar {
  width: 52px;
  height: 52px;
  background-color: #9e9e9e; /* 預設灰色 */
  color: #fff;
  border-radius: 50%;
  display: flex;
  font-size: 24px;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s;
}
.avatar.active {
  background-color: #6f42c1; /* 紫色 */
}

.account-detail {
  font-family: 'Mulish';
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 23px;
  color: #051C2C;
}

.account-text{
  font-family: 'Euclid Square';
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
  line-height: 25px;
  display: flex;
  align-items: center;

  color: #051C2C;
}

.bank-text{
  font-family: 'Euclid Square';
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 23px;
  display: flex;
  align-items: center;

  color: #051C2C;
}

/* Auto pay 開關樣式 */
.auto-pay-section {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  min-width: 120px;
}

.auto-pay-label {
  font-family: 'Euclid Square';
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 23px;
  color: #051C2C;
  white-space: nowrap;
  flex-shrink: 0;
}

.auto-pay-toggle {
  display: flex;
  align-items: center;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #fff;
  border: 2px solid #22313f;
  border-radius: 24px;
  transition: 0.2s;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 2px;
  bottom: 2px;
  background-color: #22313f;
  border-radius: 50%;
  transition: 0.2s;
}

.toggle-switch input:checked + .toggle-slider {
  border: 2px solid #00C962;
  background: #fff;
}

.toggle-switch input:checked + .toggle-slider:before {
  background-color: #00C962;
  left: 22px;
}

.withdraw-bank-title{ 
  font-family: 'Euclid Square';
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
  line-height: 25px;
  display: flex;
  align-items: center;
  color: #051C2C;
}
.withdraw-subtitle{
  font-family: 'Mulish';
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 23px;
  color: #051C2C;
}
</style>
